// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { ChargeListItem } from '@/billing/api/types';
import { ChargeDetailRow } from '@/billing/components/charge-preview-card';
import { useMarkChargeBillable } from '@/billing/hooks/use-mark-charge-billable';
import { FormCurrencyTextField } from '@/components/form-currency-text-field';
import {
  SerializableCurrency,
  currencyInputSchema,
} from '@/currency/api/types';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { CurrencyDecimal } from '@/currency/utilities/decimal';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { toSerializableCurrency } from '@/currency/utilities/to-form-value';
import { formatShortDate } from '@/utilities/date-time';
import { formatNumber } from '@/utilities/number';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { Decimal } from 'decimal.js';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

interface Props {
  isOpen: boolean;
  charge: ChargeListItem | null;
  onClose: () => void;
  onError?: (
    message: string,
    apiStatusCode: number,
    httpStatusCode: number
  ) => void;
}

interface PreviewState {
  isSplit: boolean;
  // Post-state of the leftover slice that stays on the original charge after
  // the operation. For no-split this collapses to the no-op zero state and is
  // not rendered; for split this is what the original charge looks like after
  // its quantity / total are reduced. The leftover always keeps the
  // original's expected bill date and has no eligible bill date stamped.
  leftoverQuantity: number;
  leftoverTotal: string;
  leftoverExpectedBillDate: DateTime | null;
  // Post-state of the slice being billed now. For no-split this is the
  // in-place charge; for split this is the spawned BILLABLE charge. The
  // expected bill date is always inherited from the original charge; only
  // billedBillingEligibleDate is user-supplied.
  billedQuantity: number;
  billedTotal: string;
  billedExpectedBillDate: DateTime | null;
  billedBillingEligibleDate: DateTime | null;
}

interface ValidationState {
  errors: string[];
  isValid: boolean;
}

interface OverrideFormValues {
  override: SerializableCurrency;
}

// Label column width used for the outcome rows and the context box's right
// column. Chosen to fit "Billing eligible date" (the widest label in the
// outcome) with a small buffer, so values stay visually aligned in a grid.
// ChargeDetailRow's default (96) is too narrow once labels get this long.
const OUTCOME_LABEL_MIN_WIDTH = 140;

// Returns the exact difference (a − b) of two validated amount strings.
function subtractCurrencyStrings(a: string, b: string): string {
  return new CurrencyDecimal(a).minus(b).toString();
}

// Returns the exact product of a validated amount string and a quantity. Used
// for the UNIT preview where total = unit_price × quantity.
function multiplyCurrencyStringByInt(value: string, qty: number): string {
  return new CurrencyDecimal(value).times(qty).toString();
}

// Seeds the billing eligible date when the dialog opens. The charge "becomes
// eligible" at the earlier of its scheduled bill date and today: late charges
// default to the originally scheduled date, while on-schedule or early
// charges default to today. The user can still override either way.
function defaultBillingEligibleDate(
  expectedBillableDate: DateTime | null
): DateTime {
  const today = DateTime.now().startOf('day');
  if (
    expectedBillableDate &&
    expectedBillableDate.isValid &&
    expectedBillableDate < today
  ) {
    return expectedBillableDate.startOf('day');
  }
  return today;
}

// Renders a DateTime as the shared locale-style date string used across the
// charges table and preview cards, or an em dash when null. Using
// formatShortDate keeps every display row in the dialog (context card rows,
// split outcome rows) in one format.
function formatDateOrDash(value: DateTime | null): string {
  return value && value.isValid ? formatShortDate(value) : '\u2014';
}

// Formats a bare cent-precise amount string (the override math output) as a
// display value in the charge's single currency.
function formatChargeMoney(
  amount: string,
  currencyCode: IsoCurrencyCode | null
): string {
  if (currencyCode === null) {
    return '';
  }

  return formatCurrencyValue({
    amount: new Decimal(amount),
    isoCode: currencyCode,
  });
}

/**
 * Confirms marking a PLANNED charge as BILLABLE. Captures the bill date and
 * the quantity being billed now; for FIXED-priced charges, also captures an
 * override total. When the requested quantity is less than the charge's
 * current quantity the dialog presents a split preview — the original keeps
 * its bill date with reduced quantity, and a new BILLABLE charge is created
 * for the remainder.
 *
 * Currency values are kept as decimal strings end-to-end and validated with
 * currencyInputSchema; all math is exact Decimal arithmetic.
 */
export function MarkChargeBillableDialog(props: Props) {
  const charge = props.charge;
  const isFixed = charge?.pricingMode === 'FIXED';
  const currentQty = charge?.quantity ?? 0;
  // The charge's amount is a currency object; the override math runs on the
  // bare amount string while the ISO drives the symbol and the serialized
  // payload. A charge always carries an amount (totalPrice is non-null).
  const currentTotal = charge
    ? toSerializableCurrency(charge.totalPrice).amount
    : '0.00';
  const chargeCurrencyCode = charge?.totalPrice.isoCode ?? null;
  const minOverrideDisplay = formatChargeMoney('0.00', chargeCurrencyCode);
  const currentTotalDisplay = formatChargeMoney(
    currentTotal,
    chargeCurrencyCode
  );
  const currentExpectedBillableDate = charge?.expectedBillableDate ?? null;

  // Billing eligible date is what the user is setting — the moment the charge
  // actually became eligible to bill. Defaults to MIN(expected bill date,
  // today) on open so late charges land on their original schedule. The
  // charge's scheduled expected bill date is shown read-only in the context
  // card and is never mutated by this flow.
  const [billingEligibleDate, setBillingEligibleDate] =
    useState<DateTime | null>(
      defaultBillingEligibleDate(currentExpectedBillableDate)
    );
  const [quantityInput, setQuantityInput] = useState<string>(
    String(currentQty)
  );
  const overrideForm = useForm<OverrideFormValues>({
    defaultValues: charge
      ? { override: toSerializableCurrency(charge.totalPrice) }
      : undefined,
  });
  const resetOverrideForm = overrideForm.reset;
  const overrideInput = useWatch({
    control: overrideForm.control,
    name: 'override',
  });

  // Re-seed defaults whenever the dialog opens or a new charge target is set.
  // Watching isOpen (not just charge identity) catches the case where the user
  // reopens the dialog with the same charge after editing inputs and closing
  // — the render-at-root pattern keeps this component mounted across opens, so
  // a charge-only dependency would not re-fire and the form would show stale
  // state from the previous session.
  useEffect(() => {
    if (!props.isOpen || !charge) {
      return;
    }
    setBillingEligibleDate(
      defaultBillingEligibleDate(charge.expectedBillableDate)
    );
    setQuantityInput(String(charge.quantity));
    resetOverrideForm({ override: toSerializableCurrency(charge.totalPrice) });
  }, [props.isOpen, charge, resetOverrideForm]);

  const close = () => {
    props.onClose();
  };

  const mutation = useMarkChargeBillable({
    onSuccess: () => {
      close();
    },
    onError: (message, apiStatusCode, httpStatusCode) => {
      close();
      if (apiStatusCode === ERROR_CODE_MAPPING.plan_limit_exceeded) {
        return;
      }

      props.onError?.(message, apiStatusCode, httpStatusCode);
    },
  });

  // ---------------------------------------------------------------------------
  // Validation + preview computation. Both run on every render so the dialog
  // can show inline errors and the preview stays live as the user types.
  // ---------------------------------------------------------------------------

  const parsedQty = Number(quantityInput);
  const isSplit =
    Number.isInteger(parsedQty) && parsedQty >= 1 && parsedQty < currentQty;
  // Override only matters when we're splitting a FIXED charge — no-split paths
  // ignore it entirely (and the backend rejects it).
  const overrideEditable = isFixed && isSplit;
  const parsedOverride = useMemo(
    () => currencyInputSchema.safeParse(overrideInput),
    [overrideInput]
  );

  const validation: ValidationState = useMemo(() => {
    const errors: string[] = [];
    if (!charge) {
      return { errors, isValid: false };
    }

    // Quantity is integer-only and bounded by the existing charge.
    if (
      !Number.isInteger(parsedQty) ||
      parsedQty < 1 ||
      parsedQty > currentQty
    ) {
      errors.push(`Quantity must be an integer between 1 and ${currentQty}`);
    }

    if (!billingEligibleDate || !billingEligibleDate.isValid) {
      errors.push('Billing eligible date is required');
    }

    // Override is only validated when it's actually editable. Required for
    // FIXED splits; must satisfy currencyInputSchema; must fall in
    // [0, currentTotal].
    if (overrideEditable) {
      if (!parsedOverride.success) {
        errors.push('Amount to bill now must be a valid dollar amount');
      } else if (
        parsedOverride.data.amount.isNegative() ||
        parsedOverride.data.amount.greaterThan(charge.totalPrice.amount)
      ) {
        errors.push(
          `Amount to bill now must be between ${minOverrideDisplay} and ${currentTotalDisplay}`
        );
      }
    }

    return { errors, isValid: errors.length === 0 };
  }, [
    charge,
    parsedQty,
    currentQty,
    billingEligibleDate,
    overrideEditable,
    parsedOverride,
    minOverrideDisplay,
    currentTotalDisplay,
  ]);

  const preview: PreviewState | null = useMemo(() => {
    if (!charge || !validation.isValid || !billingEligibleDate) {
      return null;
    }

    let billedTotal: string;
    let leftoverTotal: string;
    if (isSplit) {
      // Split path: compute the billed slice and the leftover. For UNIT we
      // derive both from unit_price; for FIXED the override is the new
      // slice's total and the leftover absorbs the rest (money conservation).
      if (isFixed) {
        billedTotal = overrideInput.amount;
        leftoverTotal = subtractCurrencyStrings(
          currentTotal,
          overrideInput.amount
        );
      } else {
        const unitPrice =
          charge.unitPrice === null
            ? '0.00'
            : toSerializableCurrency(charge.unitPrice).amount;
        billedTotal = multiplyCurrencyStringByInt(unitPrice, parsedQty);
        leftoverTotal = multiplyCurrencyStringByInt(
          unitPrice,
          currentQty - parsedQty
        );
      }
    } else {
      // No-split path: the charge is billed in place with its existing total.
      // The override field is ignored (and disabled in the UI).
      billedTotal = currentTotal;
      leftoverTotal = '0.00';
    }

    return {
      isSplit,
      leftoverQuantity: currentQty - parsedQty,
      leftoverTotal,
      leftoverExpectedBillDate: currentExpectedBillableDate,
      billedQuantity: parsedQty,
      billedTotal,
      // Schedule is always inherited from the original — neither the
      // no-split flip nor the split changes expected_billable_date.
      billedExpectedBillDate: currentExpectedBillableDate,
      billedBillingEligibleDate: billingEligibleDate,
    };
  }, [
    charge,
    validation.isValid,
    billingEligibleDate,
    parsedQty,
    currentQty,
    isFixed,
    isSplit,
    overrideInput,
    currentTotal,
    currentExpectedBillableDate,
  ]);

  const handleConfirm = () => {
    if (!charge || !preview || !billingEligibleDate) {
      return;
    }
    mutation.mutate({
      chargeKey: charge.key,
      billingEligibleDate,
      quantity: preview.billedQuantity,
      // Override only travels on FIXED splits — the no-split path explicitly
      // rejects an override server-side and UNIT doesn't need one at all. The
      // override carries the charge's single currency.
      totalPriceOverride:
        overrideEditable && parsedOverride.success ? parsedOverride.data : null,
      // Forward the row's existing service period — the BE requires both
      // dates for milestone activation and ignores them for PERIODIC since
      // those rows already carry their service window.
      servicePeriodStart: charge.servicePeriodStart,
      servicePeriodEnd: charge.servicePeriodEnd,
    });
  };

  const isPending = mutation.isPending;
  const confirmDisabled = !validation.isValid || isPending || !charge;

  // Quantity helper text has three states: when the user reduces the quantity
  // into the split range, spell out the exact split; otherwise show the
  // invitation to split. Invalid-quantity cases also fall through to the
  // invitation copy so the helper does not flicker while the user types —
  // the warning Alert carries the error message.
  const quantityHelperText = isSplit
    ? `Splits into a new billable charge of ${parsedQty} and leaves ${currentQty - parsedQty} on the original planned charge.`
    : 'Reduce to split this charge: a new billable charge will be created for the billed quantity and the remainder stays planned.';

  // Milestone charges (trigger_type = MANUAL) don't auto-transition via the
  // cron — they're flipped by the user acknowledging a real-world event
  // reached. The action text swaps from "mark billable" to "mark as reached"
  // so the wording matches the underlying semantics without changing the
  // code path that actually writes billing_eligible_date.
  const isMilestoneCharge = charge?.triggerType === 'MANUAL';
  const dialogTitle = isMilestoneCharge
    ? 'Mark milestone reached'
    : 'Mark charge billable';
  const dialogIntro = isMilestoneCharge
    ? 'Confirm when this milestone was reached.'
    : 'Review the charge and confirm when it became billable.';
  const confirmButtonLabel = isMilestoneCharge
    ? 'Mark as reached'
    : 'Mark billable';

  return (
    <Dialog fullWidth maxWidth="md" open={props.isOpen} onClose={close}>
      <DialogTitle
        sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}
      >
        {dialogTitle}
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
          {dialogIntro}
        </Typography>

        {charge && (
          <ChargeContextBox
            charge={charge}
            billingEligibleDate={billingEligibleDate}
          />
        )}

        <Stack spacing={2} sx={{ mt: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Optional adjustments
          </Typography>
          <DatePicker
            label="Billing eligible date"
            value={billingEligibleDate}
            format="yyyy-MM-dd"
            onChange={(value) => setBillingEligibleDate(value)}
            disabled={isPending}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small',
                helperText:
                  'The date this charge became eligible to bill. Defaults to today, or the expected bill date if it has already passed.',
              },
            }}
          />
          {currentQty > 1 && (
            <TextField
              label="Quantity"
              type="number"
              value={quantityInput}
              onChange={(e) => setQuantityInput(e.target.value)}
              disabled={isPending}
              fullWidth
              size="small"
              inputProps={{ min: 1, max: currentQty, step: 1 }}
              helperText={quantityHelperText}
            />
          )}
          {overrideEditable && chargeCurrencyCode !== null && (
            <Stack spacing={0.5}>
              <FormCurrencyTextField
                name="override"
                control={overrideForm.control}
                label="Amount to bill now"
                lockTo={chargeCurrencyCode}
                compact
                disabled={isPending}
                fullWidth
              />
              <Typography variant="caption" color="text.secondary">
                {`Between ${minOverrideDisplay} and ${currentTotalDisplay}. The remainder stays on the original charge.`}
              </Typography>
            </Stack>
          )}
        </Stack>

        {validation.errors.length > 0 && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            <Stack spacing={0.5}>
              {validation.errors.map((err) => (
                <Typography key={err} variant="body2">
                  {err}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}

        {preview && preview.isSplit && validation.errors.length === 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Splits into two charges
            </Typography>
            <Stack direction="row" spacing={2}>
              <Alert
                severity="info"
                variant="outlined"
                icon={false}
                sx={{ flex: 1 }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight="bold"
                  sx={{ display: 'block', mb: 1 }}
                >
                  This charge: billable now
                </Typography>
                <Stack spacing={0.75}>
                  <ChargeDetailRow
                    label="Quantity"
                    value={formatNumber(preview.billedQuantity)}
                    labelMinWidth={OUTCOME_LABEL_MIN_WIDTH}
                  />
                  <ChargeDetailRow
                    label="Amount"
                    value={formatChargeMoney(
                      preview.billedTotal,
                      chargeCurrencyCode
                    )}
                    labelMinWidth={OUTCOME_LABEL_MIN_WIDTH}
                  />
                  <ChargeDetailRow
                    label="Expected bill date"
                    value={formatDateOrDash(preview.billedExpectedBillDate)}
                    labelMinWidth={OUTCOME_LABEL_MIN_WIDTH}
                  />
                  <ChargeDetailRow
                    label="Billing eligible date"
                    value={formatDateOrDash(preview.billedBillingEligibleDate)}
                    labelMinWidth={OUTCOME_LABEL_MIN_WIDTH}
                  />
                </Stack>
              </Alert>
              <Alert
                severity="info"
                variant="outlined"
                icon={false}
                sx={{ flex: 1 }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight="bold"
                  sx={{ display: 'block', mb: 1 }}
                >
                  Remaining charge: planned for later
                </Typography>
                <Stack spacing={0.75}>
                  <ChargeDetailRow
                    label="Quantity"
                    value={formatNumber(preview.leftoverQuantity)}
                    labelMinWidth={OUTCOME_LABEL_MIN_WIDTH}
                  />
                  <ChargeDetailRow
                    label="Amount"
                    value={formatChargeMoney(
                      preview.leftoverTotal,
                      chargeCurrencyCode
                    )}
                    labelMinWidth={OUTCOME_LABEL_MIN_WIDTH}
                  />
                  <ChargeDetailRow
                    label="Expected bill date"
                    value={formatDateOrDash(preview.leftoverExpectedBillDate)}
                    labelMinWidth={OUTCOME_LABEL_MIN_WIDTH}
                  />
                </Stack>
              </Alert>
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: 'block', textAlign: 'right', mt: 2 }}
            >
              {`${formatChargeMoney(preview.billedTotal, chargeCurrencyCode)} + ${formatChargeMoney(
                preview.leftoverTotal,
                chargeCurrencyCode
              )} = ${currentTotalDisplay}`}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}
      >
        <Button onClick={close} variant="outlined" disabled={isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={confirmDisabled}
          sx={{ ml: 1.5 }}
        >
          {confirmButtonLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Two-column context card describing the charge being acted on. Mirrors the
// shared ChargePreviewCard's rows but adds the charge label and splits the
// fields into identity/structure (left) and schedule/amounts (right) so the
// wide md dialog is not wasted as a tall single column. The right column
// also surfaces the user's current Billing eligible date choice so, in the
// no-split path, this card is the full summary — no separate outcome box is
// needed.
function ChargeContextBox(props: {
  charge: ChargeListItem;
  billingEligibleDate: DateTime | null;
}) {
  const servicePeriod =
    props.charge.servicePeriodStart && props.charge.servicePeriodEnd
      ? `${formatShortDate(props.charge.servicePeriodStart)} \u2013 ${formatShortDate(props.charge.servicePeriodEnd)}`
      : '\u2014';
  const showUnitPrice =
    props.charge.pricingMode === 'PER_UNIT' && props.charge.unitPrice !== null;
  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack direction="row" spacing={3}>
        <Stack spacing={0.75} sx={{ flex: 1 }}>
          <ChargeDetailRow label="Label" value={props.charge.label} />
          <ChargeDetailRow
            label="Customer"
            value={props.charge.project.customer?.name ?? '\u2014'}
          />
          <ChargeDetailRow label="Project" value={props.charge.project.name} />
          <ChargeDetailRow
            label="Item"
            value={`${props.charge.item.sku} (${props.charge.item.name})`}
          />
          <ChargeDetailRow label="Service period" value={servicePeriod} />
        </Stack>
        <Stack spacing={0.75} sx={{ flex: 1 }}>
          <ChargeDetailRow
            label="Quantity"
            value={formatNumber(props.charge.quantity)}
            labelMinWidth={OUTCOME_LABEL_MIN_WIDTH}
          />
          {showUnitPrice && props.charge.unitPrice !== null && (
            <ChargeDetailRow
              label="Unit price"
              value={formatCurrencyValue(props.charge.unitPrice)}
              labelMinWidth={OUTCOME_LABEL_MIN_WIDTH}
            />
          )}
          <ChargeDetailRow
            label="Amount"
            value={formatCurrencyValue(props.charge.totalPrice)}
            labelMinWidth={OUTCOME_LABEL_MIN_WIDTH}
          />
          <ChargeDetailRow
            label="Expected bill date"
            value={
              props.charge.expectedBillableDate
                ? formatShortDate(props.charge.expectedBillableDate)
                : '\u2014'
            }
            labelMinWidth={OUTCOME_LABEL_MIN_WIDTH}
          />
          <ChargeDetailRow
            label="Billing eligible date"
            value={formatDateOrDash(props.billingEligibleDate)}
            labelMinWidth={OUTCOME_LABEL_MIN_WIDTH}
          />
        </Stack>
      </Stack>
    </Box>
  );
}
